import { Box, Divider, Grid, List, Paper } from '@mui/material';
import { useState } from 'react';
import { FormProvider, useFormContext, SubmitHandler, FieldValues } from 'react-hook-form';
import {
  LuArrowUpNarrowWide,
  LuBookOpenCheck,
  LuBuilding,
  LuClock,
  LuFileText,
  LuGraduationCap,
  LuLanguages,
  LuMap,
  LuMapPin,
  LuSearch,
  LuStamp,
} from 'react-icons/lu';
import { TbBriefcase } from 'react-icons/tb';
import { MonsterSearch } from 'wisestep-api';
import { IFilterList, WsButton, WsColors, WsFilterTags, WsSelect } from 'wisestep-base';

import CheckboxListFilter from '../../../../components/filters/components/CheckboxListFilter';
import JobTitleFilter from '../../../../components/filters/components/JobTitleFilter';
import SalaryRangeFilter from '../../../../components/filters/components/SalaryRangeFilter';
import WsAccordion from '../../../../components/filters/components/WsAccordion';
import AdvancedFiltersHeader from '../../../../utils/AdvancedFiltersHeader';
import BasicSearchForm from '../../types/IBasicSearchForm';
import {
  careerLevelCheckBoxOptions,
  desiredSalaryOptions,
  desiredSalaryTimeSpanOptions,
  educationLevelOptions,
  globalLevelSecurityCheckBoxOptions,
  globalMilitaryCheckBoxOptions,
  KeyWordQueryOptions,
  languageOptions,
  languageProficiencyOptions,
  managerialExperienceOptions,
  targetJobTypeOptions,
  targetWorkIndustriesOptions,
  WillingRelocateOptions,
  workAuthorizationOptions,
  workSiteOptions,
  yearExperienceOptions,
} from '../utils/MonsterHelper';

import OccupationFilter from './OccupationFilter';
import PowerSearchAdvanceFilter from './PowerSearchAdvancedFilters';
import SelectFilters from './SelectFilter';

interface AdvanceFilterNewProps {
  performSearch: (values: BasicSearchForm & MonsterSearch) => void;
}

const AdvancedFiltersNew = ({ performSearch }: AdvanceFilterNewProps) => {
  const methods = useFormContext();
  const { handleSubmit, reset, watch, setValue, getValues } = methods;
  const searchType = watch('searchType');
  const [languageTags, setLanguageTags] = useState<IFilterList[]>([]);
  const getNewLanguageTag = (languageIndex: string, proficiencyIndex: string) => {
    return {
      tag: `${languageOptions.find((item) => item.value == languageIndex)?.label} : ${
        languageProficiencyOptions.find((item) => item.value == proficiencyIndex)?.label
      }`,
      id: languageIndex,
    };
  };

  const onDeleteLanguageTag = (tag: string) => {
    setLanguageTags((prevTags) => {
      const newTags = prevTags.filter((languageTag) => languageTag.tag !== tag);

      const newLSkillValues = newTags.map((item) => {
        const languageLabel = item.tag.split(' : ')[0];
        const proficiencyLabel = item.tag.split(' : ')[1];
        const languageValue = languageOptions.find((item2) => item2.label === languageLabel)?.value;
        const proficiencyValue = languageProficiencyOptions.find(
          (item2) => item2.label === proficiencyLabel
        )?.value;

        return `${languageValue}-${proficiencyValue}`;
      });

      setValue('lskill', newLSkillValues);
      return newTags;
    });
  };

  const addLanguage = () => {
    const language = getValues('language');
    const proficiency = getValues('proficiency');

    const lskillValue = getValues('lskill');
    const lskillArray = lskillValue ? lskillValue.split(',') : [];
    lskillArray.push(`${language}-${proficiency}`);
    const lskillString = lskillArray.join(',');
    setValue('lskill', lskillString);

    setLanguageTags([...languageTags, getNewLanguageTag(language, proficiency)]);
  };

  return (
    <FormProvider {...methods}>
      <Paper sx={{ mt: 2 }}>
        <Box
          component="form"
          role="presentation"
          onSubmit={handleSubmit(performSearch as SubmitHandler<FieldValues>)}
          onReset={() => reset()}
        >
          <AdvancedFiltersHeader />
          <Divider />
          {searchType === 'Boolean' ? (
            <List>
              <SelectFilters
                name="qb"
                title="Keyword query behavior"
                selectArray={KeyWordQueryOptions}
                startIcon={<LuSearch color={WsColors.TertiaryShades800} />}
              />
              <JobTitleFilter
                name="qrjtArr"
                placeHolder="Recent Job Title"
                title="Recent Job Title"
                showAccordionIcon={true}
              />
              <JobTitleFilter
                name="qtjtArr"
                placeHolder="Target Job Title"
                title="Target Job Title"
                showAccordionIcon={true}
              />
              <JobTitleFilter
                name="qrcnArr"
                placeHolder="Recent Company Name"
                title="Recent Company Name"
                icon={<LuBuilding color={WsColors.TertiaryShades800} />}
              />
              <JobTitleFilter
                name="qrjdArr"
                placeHolder="Recent Job Description"
                title="Recent Job Description"
                icon={<LuFileText color={WsColors.TertiaryShades800} />}
              />
              <JobTitleFilter
                name="qsnArr"
                placeHolder="Recent school name"
                title="Recent School Name"
                icon={<LuGraduationCap color={WsColors.TertiaryShades800} />}
              />
              <SelectFilters
                name="targetJobType"
                title="Target Job Type"
                selectArray={targetJobTypeOptions}
                startIcon={<TbBriefcase color={WsColors.TertiaryShades800} />}
              />
              <SelectFilters
                name="edulv"
                title="Education level"
                selectArray={educationLevelOptions}
                startIcon={<LuGraduationCap color={WsColors.TertiaryShades800} />}
              />
              <SelectFilters
                name="relo"
                title="Willing to Relocate"
                selectArray={WillingRelocateOptions}
                startIcon={<LuMap color={WsColors.TertiaryShades800} />}
              />
              <SelectFilters
                name="twsite"
                title="Work Site"
                selectArray={workSiteOptions}
                startIcon={<LuMapPin color={WsColors.TertiaryShades800} />}
              />
              <WsAccordion
                title="Work authorization"
                startIcon={<LuMap color={WsColors.TertiaryShades800} />}
              >
                <WsSelect name="wa" selectArray={workAuthorizationOptions} placeholder="Select" />
              </WsAccordion>

              <SalaryRangeFilter
                title="Desired Salary"
                selectArray={desiredSalaryOptions}
                selectArray1={desiredSalaryTimeSpanOptions}
                showAccordionIcon={true}
              />
              <SelectFilters
                name="mgrex"
                title="Managerial Experience"
                selectArray={managerialExperienceOptions}
                startIcon={<LuClock color={WsColors.TertiaryShades800} />}
              />
              <SelectFilters
                name="yrsexpid"
                title="Years of Experience"
                selectArray={yearExperienceOptions}
                startIcon={<LuClock color={WsColors.TertiaryShades800} />}
              />
              <WsAccordion
                title="Language"
                startIcon={<LuLanguages color={WsColors.TertiaryShades800} />}
              >
                <Grid container spacing={2} direction="row">
                  <Grid item xs={5}>
                    <WsSelect
                      name="language"
                      selectArray={languageOptions}
                      placeholder="Language"
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <WsSelect
                      name="proficiency"
                      selectArray={languageProficiencyOptions}
                      placeholder="Proficiency"
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <WsButton variant="contained" size="small" onClick={addLanguage}>
                      Add
                    </WsButton>
                  </Grid>
                </Grid>
                <WsFilterTags
                  filtersList={languageTags}
                  onDelete={onDeleteLanguageTag}
                  label="Languages to be filtered"
                />
              </WsAccordion>
              <SelectFilters
                name="twindus"
                title="Target Work Industry"
                selectArray={targetWorkIndustriesOptions}
                startIcon={<LuBookOpenCheck color={WsColors.TertiaryShades800} />}
              />
              <OccupationFilter
                title="Target Occupation"
                name="tcc"
                placeHolder="Job Category"
                placeHolder1="Occupation"
                startIcon={<TbBriefcase color={WsColors.TertiaryShades800} />}
              />
              <CheckboxListFilter
                name="clvArr"
                options={careerLevelCheckBoxOptions}
                title="Career Level"
                startIcon={<LuArrowUpNarrowWide color={WsColors.TertiaryShades800} />}
              />
              <CheckboxListFilter
                name="gscArr"
                options={globalLevelSecurityCheckBoxOptions}
                title="Global Security Clearance"
                startIcon={<LuStamp color={WsColors.TertiaryShades800} />}
              />
              <CheckboxListFilter
                name="gmiArr"
                options={globalMilitaryCheckBoxOptions}
                title="Global Military Status"
                startIcon={<LuStamp color={WsColors.TertiaryShades800} />}
              />
            </List>
          ) : (
            <PowerSearchAdvanceFilter />
          )}
        </Box>
      </Paper>
    </FormProvider>
  );
};

export default AdvancedFiltersNew;
